import {
  AfterViewInit,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { environment } from '@myenv/environment';
import { MarkerClusterer, Cluster } from '@googlemaps/markerclusterer';
import { DemoDataService } from '@shared/demo-data/demo-data.service';
import { LiveService } from '@components/live/live.service';
import { DeviceService } from '@components/device/device.service';
import { GroupsService } from '@components/device/groups/groups.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonDropdownComponent } from '@shared/button-dropdown/button-dropdown.component';
import { DeviceNavigationService } from '@shared/device-navigation.service';
import { configMessage } from '@shared/config-message';
import moment from 'moment';

declare const google: any;

export type DeviceStatus = 'online' | 'idle' | 'offline' | 'disconnected';

export interface LiveDevice {
  id: string;
  status: DeviceStatus;
  lastEvent: string;
  lastEventIcon: string;
  timeAgo: string;
  lat: number;
  lng: number;
  vehicleColor: 'green' | 'orange' | 'red' | 'gray';
  active: boolean;
  online: boolean;
  lastKnownLocation: string;
  lastKnownEventDate: string;
  tripStatus: 'In Trip' | 'Idle' | 'Trip End' | 'Disconnected';
  tripSince: string;
  tripDistance: string;
  tripDuration: string;
  battery: number;
  gpsStatus: 'Available' | 'Unavailable';
  communication: 'Online' | 'Offline';
  deviceId?: string;
  serialNumber?: string;
  vin?: string;
  make?: string;
  model?: string;
  year?: string;
  assetName?: string;
  eventTime?: number;
  locationResolved?: boolean;
}

const LIVE_ATTRIBUTES =
  'serialNumber,assetName,deviceId,vin,latitude,longitude,lastCommunication,lastKnownEvent,odometer,tripNumber,generation';

interface LiveDeviceFocus {
  deviceId?: string;
  serialNumber?: string;
  assetName?: string;
  groupId?: string;
}

interface VehicleMessage {
  serialNumber?: string;
  assetName?: string;
  deviceId?: string;
  vin?: string;
  latitude?: number;
  longitude?: number;
  lastCommunication?: number | string;
  lastKnownEvent?: string;
  odometer?: number;
  tripNumber?: number;
  generation?: string;
}

interface TripRecord {
  tsTime?: number;
  teTime?: number;
  distanceTravelled?: number;
}

interface StatusCount {
  communicating: number;
  notCommunicating: number;
  disconnected: number;
}

interface Group {
  value: string;
  label: string;
}

const KM_TO_MILES = 0.6214;


/**
 * Marker class added to `<body>` while this component is active so the
 * global layout containers (`.sm-content-inner`, `.body-scroll`) become flex
 * columns. That lets this page inherit a real height through the parent
 * chain instead of relying on `100vh` (which is incorrect under the global
 * `html { zoom: 0.8 }` rule applied at widths <= 1400px).
 */
const LIVE_NEW_BODY_CLASS = 'app-live-new-active';

@Component({
  selector: 'app-live-new',
  templateUrl: './live-new.component.html',
  styleUrls: ['./live-new.layout.scss', './live-new.devices.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class LiveNewComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapCanvas', { static: false }) mapCanvasRef: ElementRef<HTMLDivElement>;
  @ViewChild('devicesList', { static: false }) devicesListRef: ElementRef<HTMLUListElement>;
  @ViewChild('customerDropdown') customerDropdown?: ButtonDropdownComponent;

  fleetOptions = ['Metro Fleet', 'North Region', 'South Region'];
  selectedFleet = 'Metro Fleet';
  fleetDropdownOpen = false;

  refreshOptions = [
    { label: '15s', value: 15 },
    { label: '30s', value: 30 },
    { label: '1m', value: 60 },
    { label: '5m', value: 300 },
  ];
  selectedRefresh = this.refreshOptions[1];
  refreshDropdownOpen = false;

  groupByOptions = ['Trip Status'];
  selectedGroupBy = 'Trip Status';
  groupByDropdownOpen = false;

  statusFilterOptions = ['All', 'Trip End', 'Disconnected']; //'In Trip',
  selectedStatusFilter = 'All';
  statusFilterDropdownOpen = false;

  selectedCommunicationFilter: 'all' | 'communicating' | 'notCommunicating' | 'disconnected' = 'all';

  totalDeviceCount = 128;
  visibleDeviceCount = 10;

  searchQuery = '';

  groupOptions: Group[] = [];
  selectedGroupId: string = '';

  statusCount: StatusCount = {
    communicating: 0,
    notCommunicating: 0,
    disconnected: 0,
  };

  selectedDevice: LiveDevice | null = null;

  /** Mock list of live devices. */
  devices: LiveDevice[] = [
    {
      id: '252270503',
      status: 'online',
      lastEvent: 'GPS Message',
      lastEventIcon: 'broadcast',
      timeAgo: '3 min ago',
      lat: 13.0072,
      lng: 77.5894,
      vehicleColor: 'green',
      active: true,
      online: true,
      lastKnownLocation: 'Whitefield, Bengaluru, Karnataka',
      lastKnownEventDate: '18 May 2025, 1:40 PM',
      tripStatus: 'In Trip',
      tripSince: '12:15 PM',
      tripDistance: '32.4 km',
      tripDuration: '1h 12m',
      battery: 74,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '251372460',
      status: 'online',
      lastEvent: 'Trip Start',
      lastEventIcon: 'play',
      timeAgo: '6 min ago',
      lat: 12.9885,
      lng: 77.6072,
      vehicleColor: 'green',
      active: true,
      online: true,
      lastKnownLocation: 'Indiranagar, Bengaluru, Karnataka',
      lastKnownEventDate: '18 May 2025, 1:37 PM',
      tripStatus: 'In Trip',
      tripSince: '01:31 PM',
      tripDistance: '8.6 km',
      tripDuration: '12m',
      battery: 88,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '251837021',
      status: 'idle',
      lastEvent: 'Idling',
      lastEventIcon: 'clock',
      timeAgo: '12 min ago',
      lat: 12.9352,
      lng: 77.6245,
      vehicleColor: 'orange',
      active: true,
      online: true,
      lastKnownLocation: 'Koramangala, Bengaluru, Karnataka',
      lastKnownEventDate: '18 May 2025, 1:31 PM',
      tripStatus: 'Idle',
      tripSince: '01:19 PM',
      tripDistance: '0.0 km',
      tripDuration: '12m',
      battery: 61,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '2503872223',
      status: 'online',
      lastEvent: 'Ignition On',
      lastEventIcon: 'key',
      timeAgo: '18 min ago',
      lat: 12.9784,
      lng: 77.5946,
      vehicleColor: 'green',
      active: true,
      online: true,
      lastKnownLocation: 'MG Road, Bengaluru, Karnataka',
      lastKnownEventDate: '18 May 2025, 1:25 PM',
      tripStatus: 'In Trip',
      tripSince: '01:25 PM',
      tripDistance: '2.1 km',
      tripDuration: '18m',
      battery: 92,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '2518170129',
      status: 'disconnected',
      lastEvent: 'Disconnect',
      lastEventIcon: 'unlink',
      timeAgo: '2h ago',
      lat: 12.9698,
      lng: 77.7499,
      vehicleColor: 'red',
      active: false,
      online: false,
      lastKnownLocation: 'Whitefield, Bengaluru, Karnataka',
      lastKnownEventDate: '18 May 2025, 11:40 AM',
      tripStatus: 'Disconnected',
      tripSince: '11:40 AM',
      tripDistance: '0.0 km',
      tripDuration: '2h 03m',
      battery: 14,
      gpsStatus: 'Unavailable',
      communication: 'Offline',
    },
    {
      id: '2501478821',
      status: 'idle',
      lastEvent: 'Ignition Off',
      lastEventIcon: 'key',
      timeAgo: '2h 15m ago',
      lat: 12.913,
      lng: 77.5755,
      vehicleColor: 'orange',
      active: true,
      online: true,
      lastKnownLocation: 'Jayanagar, Bengaluru, Karnataka',
      lastKnownEventDate: '18 May 2025, 11:25 AM',
      tripStatus: 'Idle',
      tripSince: '11:25 AM',
      tripDistance: '0.0 km',
      tripDuration: '2h 15m',
      battery: 47,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '2516670022',
      status: 'online',
      lastEvent: 'GPS Message',
      lastEventIcon: 'broadcast',
      timeAgo: '5 min ago',
      lat: 13.0314,
      lng: 77.5687,
      vehicleColor: 'green',
      active: true,
      online: true,
      lastKnownLocation: 'Yelahanka, Bengaluru, Karnataka',
      lastKnownEventDate: '18 May 2025, 1:38 PM',
      tripStatus: 'In Trip',
      tripSince: '12:50 PM',
      tripDistance: '18.7 km',
      tripDuration: '53m',
      battery: 81,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '2519901123',
      status: 'online',
      lastEvent: 'Trip End',
      lastEventIcon: 'flag',
      timeAgo: '33 min ago',
      lat: 12.9275,
      lng: 77.6207,
      vehicleColor: 'green',
      active: true,
      online: true,
      lastKnownLocation: 'BTM Layout, Bengaluru, Karnataka',
      lastKnownEventDate: '18 May 2025, 1:10 PM',
      tripStatus: 'Trip End',
      tripSince: '12:30 PM',
      tripDistance: '15.2 km',
      tripDuration: '40m',
      battery: 66,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '2521072290',
      status: 'idle',
      lastEvent: 'Idling',
      lastEventIcon: 'clock',
      timeAgo: '1h ago',
      lat: 12.9542,
      lng: 77.4912,
      vehicleColor: 'orange',
      active: true,
      online: true,
      lastKnownLocation: 'Banashankari, Bengaluru, Karnataka',
      lastKnownEventDate: '18 May 2025, 12:40 PM',
      tripStatus: 'Idle',
      tripSince: '12:40 PM',
      tripDistance: '0.0 km',
      tripDuration: '1h 03m',
      battery: 38,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '2522861442',
      status: 'online',
      lastEvent: 'GPS Message',
      lastEventIcon: 'broadcast',
      timeAgo: '4 min ago',
      lat: 12.9583,
      lng: 77.7188,
      vehicleColor: 'green',
      active: true,
      online: true,
      lastKnownLocation: 'Marathahalli, Bengaluru, Karnataka',
      lastKnownEventDate: '18 May 2025, 1:39 PM',
      tripStatus: 'In Trip',
      tripSince: '01:08 PM',
      tripDistance: '6.4 km',
      tripDuration: '31m',
      battery: 71,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '2604812390',
      status: 'online',
      lastEvent: 'GPS Message',
      lastEventIcon: 'broadcast',
      timeAgo: '2 min ago',
      lat: 17.4483,
      lng: 78.3741,
      vehicleColor: 'green',
      active: true,
      online: true,
      lastKnownLocation: 'Hitech City, Hyderabad, Telangana',
      lastKnownEventDate: '18 May 2025, 1:41 PM',
      tripStatus: 'In Trip',
      tripSince: '12:45 PM',
      tripDistance: '22.1 km',
      tripDuration: '56m',
      battery: 83,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '2605914402',
      status: 'online',
      lastEvent: 'Trip Start',
      lastEventIcon: 'play',
      timeAgo: '8 min ago',
      lat: 17.4156,
      lng: 78.3418,
      vehicleColor: 'green',
      active: true,
      online: true,
      lastKnownLocation: 'Gachibowli, Hyderabad, Telangana',
      lastKnownEventDate: '18 May 2025, 1:35 PM',
      tripStatus: 'In Trip',
      tripSince: '01:35 PM',
      tripDistance: '3.4 km',
      tripDuration: '8m',
      battery: 95,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '2607185591',
      status: 'idle',
      lastEvent: 'Idling',
      lastEventIcon: 'clock',
      timeAgo: '11 min ago',
      lat: 17.4324,
      lng: 78.4078,
      vehicleColor: 'orange',
      active: true,
      online: true,
      lastKnownLocation: 'Jubilee Hills, Hyderabad, Telangana',
      lastKnownEventDate: '18 May 2025, 1:32 PM',
      tripStatus: 'Idle',
      tripSince: '01:12 PM',
      tripDistance: '14.8 km',
      tripDuration: '20m',
      battery: 64,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '2601140922',
      status: 'disconnected',
      lastEvent: 'Disconnect',
      lastEventIcon: 'unlink',
      timeAgo: '3h ago',
      lat: 17.5008,
      lng: 78.3852,
      vehicleColor: 'red',
      active: false,
      online: false,
      lastKnownLocation: 'Kukatpally, Hyderabad, Telangana',
      lastKnownEventDate: '18 May 2025, 10:40 AM',
      tripStatus: 'Disconnected',
      tripSince: '10:40 AM',
      tripDistance: '0.0 km',
      tripDuration: '3h 00m',
      battery: 8,
      gpsStatus: 'Unavailable',
      communication: 'Offline',
    },
    {
      id: '2608914561',
      status: 'online',
      lastEvent: 'GPS Message',
      lastEventIcon: 'broadcast',
      timeAgo: '4 min ago',
      lat: 17.4399,
      lng: 78.4483,
      vehicleColor: 'green',
      active: true,
      online: true,
      lastKnownLocation: 'Begumpet, Hyderabad, Telangana',
      lastKnownEventDate: '18 May 2025, 1:39 PM',
      tripStatus: 'In Trip',
      tripSince: '01:05 PM',
      tripDistance: '11.2 km',
      tripDuration: '34m',
      battery: 79,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '2603348810',
      status: 'idle',
      lastEvent: 'Ignition Off',
      lastEventIcon: 'key',
      timeAgo: '1h 30m ago',
      lat: 17.4504,
      lng: 78.5026,
      vehicleColor: 'orange',
      active: true,
      online: true,
      lastKnownLocation: 'Secunderabad, Hyderabad, Telangana',
      lastKnownEventDate: '18 May 2025, 12:12 PM',
      tripStatus: 'Idle',
      tripSince: '12:12 PM',
      tripDistance: '0.0 km',
      tripDuration: '1h 30m',
      battery: 52,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: '2609918734',
      status: 'online',
      lastEvent: 'Ignition On',
      lastEventIcon: 'key',
      timeAgo: '15 min ago',
      lat: 17.3616,
      lng: 78.4746,
      vehicleColor: 'green',
      active: true,
      online: true,
      lastKnownLocation: 'Charminar Region, Hyderabad, Telangana',
      lastKnownEventDate: '18 May 2025, 1:28 PM',
      tripStatus: 'In Trip',
      tripSince: '01:28 PM',
      tripDistance: '4.7 km',
      tripDuration: '15m',
      battery: 91,
      gpsStatus: 'Available',
      communication: 'Online',
    },
    {
      id: "2602281145",
      status: "idle",
      lastEvent: "Idling",
      lastEventIcon: "clock",
      timeAgo: "50 min ago",
      lat: 17.4842,
      lng: 78.5534,
      vehicleColor: "orange",
      active: true,
      online: true,
      lastKnownLocation: "Malkajgiri, Hyderabad, Telangana",
      lastKnownEventDate: "18 May 2025, 12:52 PM",
      tripStatus: "Idle",
      tripSince: "12:52 PM",
      tripDistance: "0.0 km",
      tripDuration: "50m",
      battery: 41,
      gpsStatus: "Available",
      communication: "Online"
    },
    {
      id: "2607718823",
      status: "online",
      lastEvent: "GPS Message",
      lastEventIcon: "broadcast",
      timeAgo: "1 min ago",
      lat: 17.3730,
      lng: 78.5476,
      vehicleColor: "green",
      active: true,
      online: true,
      lastKnownLocation: "Dilsukhnagar, Hyderabad, Telangana",
      lastKnownEventDate: "18 May 2025, 1:42 PM",
      tripStatus: "In Trip",
      tripSince: "01:15 PM",
      tripDistance: "9.8 km",
      tripDuration: "28m",
      battery: 70,
      gpsStatus: "Available",
      communication: "Online"
    },
    {
      id: "3104471900",
      status: "online",
      lastEvent: "GPS Message",
      lastEventIcon: "broadcast",
      timeAgo: "1 min ago",
      lat: 40.758,
      lng: -73.9855,
      vehicleColor: "green",
      active: true,
      online: true,
      lastKnownLocation: "Times Square, New York, NY, USA",
      lastKnownEventDate: "18 May 2025, 1:43 PM",
      tripStatus: "In Trip",
      tripSince: "01:02 PM",
      tripDistance: "12.3 mi",
      tripDuration: "42m",
      battery: 89,
      gpsStatus: "Available",
      communication: "Online"
    },
    {
      id: "3105582311",
      status: "idle",
      lastEvent: "Idling",
      lastEventIcon: "clock",
      timeAgo: "14 min ago",
      lat: 34.0522,
      lng: -118.2437,
      vehicleColor: "orange",
      active: true,
      online: true,
      lastKnownLocation: "Downtown, Los Angeles, CA, USA",
      lastKnownEventDate: "18 May 2025, 1:29 PM",
      tripStatus: "Idle",
      tripSince: "01:15 PM",
      tripDistance: "45.1 mi",
      tripDuration: "1h 30m",
      battery: 55,
      gpsStatus: "Available",
      communication: "Online"
    },
    {
      id: "3105582312",
      status: "online",
      lastEvent: "GPS Message",
      lastEventIcon: "broadcast",
      timeAgo: "12 min ago",
      lat: 36.1699,
      lng: -115.1398,
      vehicleColor: "green",
      active: true,
      online: true,
      lastKnownLocation: "Las Vegas, USA",
      lastKnownEventDate: "18 May 2025, 1:29 PM",
      tripStatus: "In Trip",
      tripSince: "01:15 PM",
      tripDistance: "12.3 mi",
      tripDuration: "12m",
      battery: 89,
      gpsStatus: "Available",
      communication: "Online"
    },
    {
      id: "4401129933",
      status: "online",
      lastEvent: "Trip Start",
      lastEventIcon: "play",
      timeAgo: "5 min ago",
      lat: 51.5074,
      lng: -0.1278,
      vehicleColor: "green",
      active: true,
      online: true,
      lastKnownLocation: "Trafalgar Square, London, UK",
      lastKnownEventDate: "18 May 2025, 1:38 PM",
      tripStatus: "In Trip",
      tripSince: "01:33 PM",
      tripDistance: "1.8 km",
      tripDuration: "5m",
      battery: 94,
      gpsStatus: "Available",
      communication: "Online"
    },
    {
      id: "4402237744",
      status: "disconnected",
      lastEvent: "Disconnect",
      lastEventIcon: "unlink",
      timeAgo: "4h ago",
      lat: 53.4808,
      lng: -2.2426,
      vehicleColor: "red",
      active: false,
      online: false,
      lastKnownLocation: "City Centre, Manchester, UK",
      lastKnownEventDate: "18 May 2025, 9:43 AM",
      tripStatus: "Disconnected",
      tripSince: "09:43 AM",
      tripDistance: "0.0 km",
      tripDuration: "4h 00m",
      battery: 5,
      gpsStatus: "Unavailable",
      communication: "Offline"
    },
    {
      id: "9718821033",
      status: "online",
      lastEvent: "GPS Message",
      lastEventIcon: "broadcast",
      timeAgo: "3 min ago",
      lat: 25.2048,
      lng: 55.2708,
      vehicleColor: "green",
      active: true,
      online: true,
      lastKnownLocation: "Downtown Dubai, UAE",
      lastKnownEventDate: "18 May 2025, 1:40 PM",
      tripStatus: "In Trip",
      tripSince: "12:50 PM",
      tripDistance: "38.2 km",
      tripDuration: "50m",
      battery: 77,
      gpsStatus: "Available",
      communication: "Online"
    },
    {
      id: "9719934422",
      status: "idle",
      lastEvent: "Ignition Off",
      lastEventIcon: "key",
      timeAgo: "1h 10m ago",
      lat: 24.4539,
      lng: 54.3773,
      vehicleColor: "orange",
      active: true,
      online: true,
      lastKnownLocation: "Corniche Road, Abu Dhabi, UAE",
      lastKnownEventDate: "18 May 2025, 12:33 PM",
      tripStatus: "Idle",
      tripSince: "12:33 PM",
      tripDistance: "0.0 km",
      tripDuration: "1h 10m",
      battery: 62,
      gpsStatus: "Available",
      communication: "Online"
    }
  ];

  /** Last polled timestamp display string. */
  lastPolledLabel = 'Last known GPS and latest device activity';

  /** Map handle (lazy-initialized; google maps types are loaded globally). */
  private map: any = null;
  private markerClusterer: MarkerClusterer | null = null;
  private mapMarkers: any[] = [];
  private readonly FALLBACK_CENTER = { lat: 0, lng: 0 };
  currentMapType: 'roadmap' | 'hybrid' = 'roadmap';

  /** ResizeObserver that recomputes the visible device count when the list
   * container changes height. */
  private listResizeObserver: ResizeObserver | null = null;

  /** Cached pixel height of a single device row (measured at runtime). */
  private deviceRowHeight = 56;

  private titleService = inject(Title);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);
  private demoData = inject(DemoDataService);
  private liveService = inject(LiveService);
  private deviceService = inject(DeviceService);
  private groupsService = inject(GroupsService);
  private deviceNavigation = inject(DeviceNavigationService);

  isLoadingDevices = false;
  private useLiveApi = false;
  private readonly pageLimit = environment.pageLimit;
  private autoRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private geocoder: any = null;
  private addressCache = new Map<string, string>();
  private hasFitInitialView = false;
  private pendingFocus: LiveDeviceFocus | null = null;
  private focusFetchInFlight = false;

  /** Closes any open dropdowns when the user clicks outside them. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      !this.fleetDropdownOpen &&
      !this.refreshDropdownOpen &&
      !this.groupByDropdownOpen &&
      !this.statusFilterDropdownOpen
    ) {
      return;
    }
    const target = event.target as Node | null;
    if (!target) {
      return;
    }
    const insideDropdown = this.hostRef.nativeElement
      .querySelectorAll('.ln-dropdown');
    let isInside = false;
    insideDropdown.forEach((el) => {
      if (el.contains(target)) {
        isInside = true;
      }
    });
    if (!isInside) {
      this.fleetDropdownOpen = false;
      this.refreshDropdownOpen = false;
      this.groupByDropdownOpen = false;
      this.statusFilterDropdownOpen = false;
    }
  }

  ngOnInit(): void {
    this.titleService.setTitle('Live' + (environment.title_text ?? ''));
    this.renderer.addClass(document.body, LIVE_NEW_BODY_CLASS);
    // The demo user (customerId 10 / "Demo") keeps the full static showcase
    // fleet declared above. Any other account pulls the real fleet from the
    // live API (messages/latest) and gets refresh / auto-refresh wired up.
    if (!this.demoData.isDemoUser()) {
      this.useLiveApi = true;
      this.devices = [];
      this.totalDeviceCount = 0;
      this.selectedDevice = null;
      this.pendingFocus = this.readFocusFromRoute();
      this.loadGroups();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.initMap();
      this.observeListResize();
      this.recomputeVisibleCount();
      if (this.useLiveApi) {
        this.startAutoRefresh();
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
    this.clearMarkers();
    this.map = null;
    this.listResizeObserver?.disconnect();
    this.listResizeObserver = null;
    this.renderer.removeClass(document.body, LIVE_NEW_BODY_CLASS);
  }

  /** Window resize fallback alongside the ResizeObserver. */
  @HostListener('window:resize')
  onWindowResize(): void {
    this.recomputeVisibleCount();
  }

  /** Tracks the device list height and recomputes the visible cap. */
  private observeListResize(): void {
    const listEl = this.devicesListRef?.nativeElement;
    if (!listEl || typeof ResizeObserver === 'undefined') {
      return;
    }
    this.listResizeObserver = new ResizeObserver(() => {
      this.zone.run(() => this.recomputeVisibleCount());
    });
    this.listResizeObserver.observe(listEl);
  }

  /** Computes how many device rows fit in the list container at full height
   * and updates `visibleDeviceCount`. Used to ensure no internal scroll. */
  private recomputeVisibleCount(): void {
    const listEl = this.devicesListRef?.nativeElement;
    if (!listEl) {
      return;
    }
    const sampleRow = listEl.querySelector<HTMLElement>('.ln-device');
    if (sampleRow && sampleRow.offsetHeight > 0) {
      this.deviceRowHeight = sampleRow.offsetHeight;
    }
    const available = listEl.clientHeight;
    if (available <= 0 || this.deviceRowHeight <= 0) {
      return;
    }
    const fitCount = Math.max(1, Math.floor(available / this.deviceRowHeight));
    const cap = Math.min(fitCount, this.devices.length, this.totalDeviceCount);
    if (cap !== this.visibleDeviceCount) {
      this.visibleDeviceCount = cap;
      this.cdr.markForCheck();
    }
  }

  // ---------- Filtering / Search ----------

  get notCommunicatingCount(): number {
    return (this.statusCount.notCommunicating ?? 0); // + (this.statusCount.disconnected ?? 0);
  }

  /** All devices that match the current search filter (no row cap applied). */
  get filteredDevices(): LiveDevice[] {
    const q = this.searchQuery.trim().toLowerCase();
    if (!q) {
      return this.devices;
    }
    return this.devices.filter((d) => d.id.toLowerCase().includes(q));
  }

  /** Devices actually rendered - capped to what fits in the list height. */
  get visibleDevices(): LiveDevice[] {
    return this.filteredDevices.slice(0, this.visibleDeviceCount);
  }

  // ---------- Selection ----------

  selectDevice(device: LiveDevice): void {
    if (!this.map || typeof google === 'undefined') return;

    // Check if the clicked device is already the active one
    const isSameDevice = this.selectedDevice && this.selectedDevice.id === device.id;

    if (isSameDevice) {
      // Deselect device and zoom back out to encompass all global devices dynamically
      this.selectedDevice = null;
      this.fitMapToDevices();
    } else {
      // Target this device and zoom in fully
      this.selectedDevice = device;
      this.map.panTo({ lat: device.lat, lng: device.lng });
      this.map.setZoom(19); // Max detail street-level zoom
      // Resolve a human-readable address for the details panel on demand.
      this.resolveDeviceAddress(device);
      this.loadTripStats(device);
      this.loadVehicleInfo(device);
    }
    this.drawMarkers();
    this.cdr.markForCheck();
  }

  isSelected(device: LiveDevice): boolean {
    return this.selectedDevice?.id === device.id;
  }

  closeDetailsPanel(): void {
    this.selectedDevice = null;
    // Recenter the map dynamically to show all vehicles when closing the details view
    if (this.map && typeof google !== 'undefined') {
      this.drawMarkers();
      this.fitMapToDevices();
    }
    this.cdr.markForCheck();
  }

  loadMore(): void {
    // Intentional no-op - rendered count is derived from available height.
  }

  private readFocusFromRoute(): LiveDeviceFocus | null {
    const query = this.route.snapshot.queryParams;
    const deviceId = query['deviceId'] ? String(query['deviceId']) : undefined;
    const serialNumber =
      query['serialNumber'] ||
      (query['searchBy'] === 'serialNumber' ? query['value'] : undefined);
    const assetName =
      query['assetName'] ||
      (query['searchBy'] === 'assetName' ? query['value'] : undefined);
    const groupId = query['groupId'] ? String(query['groupId']) : undefined;
    if (!deviceId && !serialNumber && !assetName && !groupId) {
      return null;
    }
    if (groupId) {
      this.groupsService.setStoredGroupId(groupId);
    }
    return {
      deviceId,
      serialNumber: serialNumber ? String(serialNumber) : undefined,
      assetName: assetName ? String(assetName) : undefined,
      groupId,
    };
  }

  private isFocusMessage(message: VehicleMessage): boolean {
    const focus = this.pendingFocus;
    if (!focus) {
      return false;
    }
    return (
      this.isSameToken(message.deviceId, focus.deviceId) ||
      this.isSameToken(message.serialNumber, focus.serialNumber) ||
      this.isSameToken(message.assetName, focus.assetName)
    );
  }

  private applyPendingFocus(): void {
    const focus = this.pendingFocus;
    if (!focus) {
      return;
    }
    const match = this.devices.find((device) => this.matchesFocus(device, focus));
    if (match) {
      this.devices = [match, ...this.devices.filter((device) => device !== match)];
      this.focusDevice(match);
      this.clearPendingFocus();
      return;
    }
    this.fetchAndFocusDevice(focus);
  }

  private matchesFocus(device: LiveDevice, focus: LiveDeviceFocus): boolean {
    return (
      this.isSameToken(device.deviceId, focus.deviceId) ||
      this.isSameToken(device.serialNumber, focus.serialNumber) ||
      this.isSameToken(device.assetName, focus.assetName) ||
      this.isSameToken(device.id, focus.serialNumber) ||
      this.isSameToken(device.id, focus.assetName)
    );
  }

  private fetchAndFocusDevice(focus: LiveDeviceFocus): void {
    if (this.focusFetchInFlight) {
      return;
    }
    const searchKey = focus.deviceId
      ? 'deviceId'
      : focus.serialNumber
        ? 'serialNumber'
        : focus.assetName
          ? 'assetName'
          : '';
    const searchValue = focus.deviceId || focus.serialNumber || focus.assetName;
    if (!searchKey || !searchValue) {
      return;
    }
    this.focusFetchInFlight = true;
    this.liveService
      .searchVehicle(null, searchValue, searchKey, undefined, {})
      .subscribe({
        next: (res: VehicleMessage[]) => {
          this.focusFetchInFlight = false;
          const list = Array.isArray(res) ? res : [];
          const raw =
            list.find((message) => this.isFocusMessage(message)) ?? list[0];
          if (!raw) {
            return;
          }
          const device = this.mapMessageToDevice(raw);
          if (!this.devices.some((item) => this.matchesFocus(item, focus))) {
            this.devices = [device, ...this.devices];
          }
          this.focusDevice(device);
          this.clearPendingFocus();
        },
        error: () => {
          this.focusFetchInFlight = false;
        },
      });
  }

  private focusDevice(device: LiveDevice): void {
    this.selectedDevice = device;
    this.panToDevice(device);
    this.resolveDeviceAddress(device);
    this.loadTripStats(device);
    this.loadVehicleInfo(device);
    this.drawMarkers();
    this.cdr.markForCheck();
  }

  private panToDevice(device: LiveDevice): void {
    if (!this.map || typeof google === 'undefined' || !this.hasValidCoords(device)) {
      return;
    }
    this.map.panTo({ lat: device.lat, lng: device.lng });
    this.map.setZoom(19);
  }

  private hasValidCoords(device: LiveDevice): boolean {
    return Number.isFinite(device.lat) && Number.isFinite(device.lng);
  }

  private clearPendingFocus(): void {
    this.pendingFocus = null;
    this.router.navigate([], {
      queryParams: {
        deviceId: null,
        serialNumber: null,
        assetName: null,
        groupId: null,
        searchBy: null,
        value: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private sortOptions(options: Group[]): Group[] {
    return [...options].sort((a, b) =>
      (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' }),
    );
  }

  private isSameToken(left?: string | number, right?: string | number): boolean {
    if (left == null || right == null || left === '' || right === '') {
      return false;
    }
    return String(left).toLowerCase() === String(right).toLowerCase();
  }

  loadGroups(): void {
    this.groupsService.getGroups().subscribe({
      next: (res: any) => {
        this.groupOptions = this.sortOptions(
          (res ?? []).map((group: any) => ({
            value: String(group.groupId),
            label: group.name,
          })),
        );
        if (this.groupOptions.length) {
          const focusGroupId = this.pendingFocus?.groupId;
          const availableIds = this.groupOptions.map((group) => group.value);
          const initialGroupId =
            focusGroupId && availableIds.includes(String(focusGroupId))
              ? String(focusGroupId)
              : this.groupsService.resolveInitialGroupId(availableIds);
          if (initialGroupId) {
            this.selectGroups(initialGroupId);
            this.cdr.markForCheck();
            return;
          }
        }
        this.loadDevices(this.selectedGroupId);
        this.loadStatusCount(this.selectedGroupId);
        this.cdr.markForCheck();
      },
      error: () => {
        console.error('Failed to load groups');
      },
    });
  }

  // ---------- Live data (messages/latest) ----------

  /**
   * Fetches the latest message per device and rebuilds the live fleet.
   * Used by the initial load, the manual "Refresh" button and the
   * auto-refresh timer.
   */
  private getLiveStatusType(): string {
    switch (this.selectedCommunicationFilter) {
      case 'communicating':
        return 'Communicating';
      case 'notCommunicating':
        return 'NotCommunicating';
      case 'disconnected':
        return 'Disconnected';
      default:
        return 'Live';
    }
  }

  private getTripStatusFilter(): { key?: string; value?: string } {
    switch (this.selectedStatusFilter) {
      case 'Trip End':
        return { key: 'tripStatus', value: 'TripEnd' };
      case 'Disconnected':
        return { key: 'tripStatus', value: 'Disconnect' };
      default:
        return {};
    }
  }

  loadDevices(groupId?: string | number, recenter = false): void {
    this.isLoadingDevices = true;
    this.cdr.markForCheck();

    const tripStatusFilter = this.getTripStatusFilter();
    this.liveService
      .getAllVehicleMessage(
        0,
        this.pageLimit,
        LIVE_ATTRIBUTES,
        groupId,
        this.getLiveStatusType(),
        tripStatusFilter.key,
        tripStatusFilter.value,
      )
      .subscribe({
        next: (res: VehicleMessage[]) => {
          const list = Array.isArray(res) ? res : [];
          const previousAddresses = this.addressCache;
          this.devices = list
            .filter((m) => m && (this.isFocusMessage(m) || (m.latitude != null && m.longitude != null)))
            .map((m) => this.mapMessageToDevice(m));

          // Reapply any addresses we already resolved so they survive a refresh.
          this.devices.forEach((d) => {
            const cached = previousAddresses.get(this.coordKey(d.lat, d.lng));
            if (cached) {
              d.lastKnownLocation = cached;
              d.locationResolved = true;
            }
          });

          // Keep the currently open details panel in sync with fresh data.
          const previousSelected = this.selectedDevice;
          const openId = previousSelected?.id;
          if (openId) {
            const match = this.devices.find((d) => d.id === openId) ?? null;
            if (match && previousSelected) {
              match.tripDistance = previousSelected.tripDistance;
              match.tripDuration = previousSelected.tripDuration;
              match.tripSince = previousSelected.tripSince;
              match.make = previousSelected.make;
              match.model = previousSelected.model;
              match.year = previousSelected.year;
            }
            this.selectedDevice = match;
            if (match) {
              this.resolveDeviceAddress(match);
              this.loadTripStats(match);
              this.loadVehicleInfo(match);
            }
          } else if (this.pendingFocus) {
            this.applyPendingFocus();
          }

          this.isLoadingDevices = false;

          this.drawMarkers();
          this.recomputeVisibleCount();

          // The map is usually initialised before the fleet arrives, leaving it
          // on the fallback center. Fit the viewport on the first load, and any
          // time a caller explicitly asks to recenter (e.g. a group change or a
          // manual refresh). Plain auto-refreshes keep the user's current
          // pan/zoom so the camera doesn't jump on every tick.
          if (recenter || !this.hasFitInitialView) {
            this.fitMapToDevices();
          }

          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoadingDevices = false;
          this.cdr.markForCheck();
        },
      });
  }

  /** Maps a single `messages/latest` record onto the view model. */
  private mapMessageToDevice(m: VehicleMessage): LiveDevice {
    const lat = Number(m.latitude);
    const lng = Number(m.longitude);
    // `lastCommunication` may arrive as an ISO-8601 string or a legacy epoch number;
    // normalise to epoch ms so the staleness/time-ago calculations work.
    const parsedEvent =
      m.lastCommunication != null && m.lastCommunication !== ''
        ? moment(m.lastCommunication)
        : null;
    const eventTime =
      parsedEvent && parsedEvent.isValid() ? parsedEvent.valueOf() : undefined;
    const messageType: string = m.lastKnownEvent || '';
    const status = this.deriveStatus(messageType, eventTime ?? null);
    const id = m.assetName || m.serialNumber || m.deviceId || '—';

    return {
      id: String(id),
      status,
      lastEvent: this.eventLabel(messageType),
      lastEventIcon: this.eventIcon(messageType),
      timeAgo: eventTime ? moment(eventTime).fromNow() : '—',
      lat,
      lng,
      vehicleColor: this.colorForStatus(status),
      active: status === 'online' || status === 'idle',
      online: status !== 'disconnected' && status !== 'offline',
      lastKnownLocation: this.coordLabel(lat, lng),
      lastKnownEventDate: eventTime
        ? moment(eventTime).format('DD MMM YYYY, h:mm A')
        : '—',
      tripStatus: this.tripStatusForMessage(messageType, status),
      tripSince: eventTime ? moment(eventTime).format('hh:mm A') : '—',
      tripDistance: '—',
      tripDuration: '—',
      battery: 0,
      gpsStatus: lat && lng ? 'Available' : 'Unavailable',
      communication:
        status === 'disconnected' || status === 'offline' ? 'Offline' : 'Online',
      deviceId: m.deviceId,
      serialNumber: m.serialNumber,
      vin: m.vin,
      assetName: m.assetName,
      eventTime,
      locationResolved: false,
    };
  }

  /**
   * Derives the coarse device state from the last message type and how stale
   * the packet is, mirroring the marker-colour logic of the old live page.
   */
  private deriveStatus(messageType: string, eventTime: number | null): DeviceStatus {
    // A real-time disconnect always wins, regardless of how recent the packet is.
    if (
      messageType === 'RealTimeDisconnect' ||
      messageType === 'RealTimeDisconnectEvent'
    ) {
      return 'disconnected';
    }
    // Communicating (green) when the last event is within 24h, otherwise
    // not communicating (offline -> yellow).
    const within24h = eventTime
      ? Date.now() - eventTime <= 24 * 60 * 60 * 1000
      : false;
    return within24h ? 'online' : 'offline';
  }

  private colorForStatus(status: DeviceStatus): LiveDevice['vehicleColor'] {
    switch (status) {
      case 'online': return 'green';
      case 'idle': return 'orange';
      case 'disconnected': return 'red';
      // offline === not communicating (>24h) -> yellow.
      default: return 'orange';
    }
  }

  /** Map marker palette aligned with the list/sidebar `vehicleColor`. */
  private markerColorForDevice(device: LiveDevice): 'green' | 'yellow' | 'red' {
    switch (device.vehicleColor) {
      case 'green': return 'green';
      case 'red': return 'red';
      default: return 'yellow';
    }
  }

  private tripStatusForMessage(
    messageType: string,
    status: DeviceStatus
  ): LiveDevice['tripStatus'] {
    if (status === 'disconnected') return 'Disconnected';
    if (messageType === 'TRIP_END' || messageType === 'TRIP_END_MESSAGE') return 'Trip End';
    // if (messageType === 'TRIP_START' || messageType === 'TRIP_START_MESSAGE') return 'In Trip';
    return 'In Trip';
  }

  /** Human-readable event label sourced from the shared config map. */
  private eventLabel(messageType: string): string {
    if (!messageType) return '—';
    return configMessage?.messageType?.[messageType] || messageType;
  }

  /** Maps a message type onto the small icon set the template understands. */
  private eventIcon(messageType: string): string {
    if (messageType === 'TRIP_START' || messageType === 'TRIP_START_MESSAGE') return 'play';
    if (messageType === 'TRIP_END' || messageType === 'TRIP_END_MESSAGE') return 'flag';
    if (messageType === 'IGNITION_ON' || messageType === 'IGNITION_OFF') return 'key';
    if (
      messageType === 'RealTimeDisconnect' ||
      messageType === 'RealTimeDisconnectEvent'
    ) {
      return 'unlink';
    }
    if (messageType === 'GPS_MESSAGE') {
      return 'broadcast';
    }
    if (messageType === 'TripData'){
      return 'location-dot';
    }
    if (messageType === 'BatteryNormalEvent'){
      return 'battery-full';
    }
    if (messageType === 'FirstPlugin'){
      return 'plug';
    }
    return 'clock';
  }

  private loadVehicleInfo(device: LiveDevice): void {
    const deviceId = device.deviceId;
    if (!deviceId) {
      return;
    }
    if (device.vin) {
      this.decodeVehicleInfo(device, device.vin);
      return;
    }
    this.deviceService.getDeviceDetail(deviceId).subscribe({
      next: (res: { vin?: string }) => {
        const vin = res?.vin;
        if (!vin) {
          return;
        }
        const target = this.devices.find((d) => d.id === device.id) ?? device;
        target.vin = vin;
        if (this.selectedDevice && this.selectedDevice.id === target.id) {
          this.selectedDevice.vin = vin;
        }
        this.decodeVehicleInfo(target, vin);
      },
      error: () => {
        console.error('Failed to load device detail for VIN');
      },
    });
  }

  private decodeVehicleInfo(device: LiveDevice, vin: string): void {
    this.liveService.getVehicleInfo(vin).subscribe({
      next: (res: any) => {
        const target = this.devices.find((d) => d.id === device.id) ?? device;
        target.make = res.make;
        target.model = res.model;
        target.year = res.year;

        if (this.selectedDevice && this.selectedDevice.id === target.id) {
          this.selectedDevice = target;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        console.error('Failed to load vehicle info');
      },
    });
  }

  private loadStatusCount(groupId?: string | number): void {
    this.liveService.getDeviceStatusCount(groupId).subscribe({
      next: (res: any) => {
        this.statusCount = {
          communicating: res.communicating,
          notCommunicating: res.notCommunicating,
          disconnected: res.disconnected,
        };
        this.totalDeviceCount =
          (res.communicating ?? 0) +
          (res.notCommunicating ?? 0) +
          (res.disconnected ?? 0);
        this.cdr.markForCheck();
      },
      error: () => {
        console.error('Failed to load status count');
      },
    });
  }

  // ---------- Trip stats (trips/device/{id}) ----------

  /**
   * Fetches the most recent trip for a device and fills in the distance /
   * duration shown in the details panel. The latest-message payload does not
   * carry these, so they come from the dedicated trips API (as on the old
   * live page). Called on selection and on each auto-refresh while open.
   */
  private loadTripStats(device: LiveDevice): void {
    const deviceId = device.deviceId;
    if (!deviceId) {
      return;
    }
    const end = Date.now();
    const start = end - 7 * 24 * 60 * 60 * 1000; // look back a week for the latest trip
    this.liveService.getTrips(deviceId, end, start, 'desc').subscribe({
      next: (res: TripRecord[]) => {
        const trips = Array.isArray(res) ? res : [];
        if (!trips.length) {
          return;
        }
        const latest = trips[0];
        // Re-resolve the live object in case the list was refreshed meanwhile.
        const target = this.devices.find((d) => d.id === device.id) ?? device;

        if (latest.distanceTravelled != null) {
          target.tripDistance =
            `${(latest.distanceTravelled * KM_TO_MILES).toFixed(1)} ${environment.distanceMetrics}`;
        }
        if (latest.tsTime) {
          const endEpoch = latest.teTime || Date.now();
          target.tripDuration = this.formatDuration(latest.tsTime, endEpoch);
          target.tripSince = moment(latest.tsTime).format('hh:mm A');
        }

        if (this.selectedDevice && this.selectedDevice.id === target.id) {
          this.selectedDevice = target;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.cdr.markForCheck();
      },
    });
  }

  /** Formats two epoch timestamps into a compact "1h 12m" style duration. */
  private formatDuration(ts: number, te: number): string {
    if (!ts || !te) {
      return '—';
    }
    let delta = Math.abs(te - ts) / 1000;
    const days = Math.floor(delta / 86400);
    delta -= days * 86400;
    const hours = Math.floor(delta / 3600) % 24;
    delta -= hours * 3600;
    const minutes = Math.floor(delta / 60) % 60;

    const parts: string[] = [];
    if (days) parts.push(`${days}d`);
    if (hours || days) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);
    return parts.join(' ');
  }

  // ---------- Reverse geocoding ----------

  private coordKey(lat: number, lng: number): string {
    return `${lat.toFixed(5)},${lng.toFixed(5)}`;
  }

  private coordLabel(lat: number, lng: number): string {
    if (!lat && !lng) return 'Location unavailable';
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }

  /**
   * Reverse-geocodes a device's coordinates into a readable address for the
   * details panel. Results are cached so repeated selections / refreshes don't
   * re-hit the Geocoder. Falls back to the raw coordinates on failure.
   */
  private resolveDeviceAddress(device: LiveDevice): void {
    if (!device || device.locationResolved || typeof google === 'undefined') {
      return;
    }
    if (!device.lat && !device.lng) {
      return;
    }

    // Force conversion to numbers
    const latNum = Number(device.lat);
    const lngNum = Number(device.lng);

    // Validate that conversion resulted in valid numbers
    if (isNaN(latNum) || isNaN(lngNum)) {
      console.error('Invalid coordinates format:', device.lat, device.lng);
      return;
    }

    const key = this.coordKey(latNum, lngNum);
    const cached = this.addressCache.get(key);
    if (cached) {
      device.lastKnownLocation = cached;
      device.locationResolved = true;
      this.cdr.markForCheck();
      return;
    }
    if (!this.geocoder) {
      this.geocoder = new google.maps.Geocoder();
    }

    this.geocoder.geocode(
      { location: { lat: latNum, lng: lngNum } }, // Used numeric values here
      (results: google.maps.GeocoderResult[] | null, statusCode: google.maps.GeocoderStatus | string) => {
        this.zone.run(() => {
          if (statusCode === 'OK' && results && results.length) {
            const address = results[0].formatted_address;
            this.addressCache.set(key, address);
            device.lastKnownLocation = address;
          } else {
            console.error('Geocoding failed with status:', statusCode);
          }
          device.locationResolved = true;
          this.cdr.markForCheck();
        });
      }
    );
  }


  // ---------- Auto refresh ----------

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    const seconds = this.selectedRefresh?.value;
    if (!seconds || seconds <= 0) {
      return;
    }
    // Keep the timer outside Angular so it doesn't trigger change detection on
    // every tick; loadDevices() re-enters the zone via the HTTP subscription.
    this.zone.runOutsideAngular(() => {
      this.autoRefreshTimer = setInterval(() => {
        this.zone.run(() => {
          // loadDevices re-syncs the open details panel (address, trip stats,
          // vehicle info) once fresh fleet data arrives.
          this.loadDevices(this.selectedGroupId);
          this.loadStatusCount(this.selectedGroupId);
        });
      }, seconds * 1000);
    });
  }

  private stopAutoRefresh(): void {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
    }
  }

  // ---------- Dropdown toggles ----------

  toggleFleetDropdown(): void {
    this.fleetDropdownOpen = !this.fleetDropdownOpen;
    this.refreshDropdownOpen = false;
  }

  selectGroups(groupId: string | number | Array<string | number>): void {
    if (Array.isArray(groupId)) return;
    this.selectedGroupId = String(groupId);
    this.groupsService.setStoredGroupId(groupId);
    this.selectedDevice = null;
    this.selectedCommunicationFilter = 'all';
    this.selectedStatusFilter = 'All';
    if (
      this.pendingFocus?.groupId &&
      String(this.pendingFocus.groupId) !== this.selectedGroupId
    ) {
      this.pendingFocus = null;
    }
    this.loadDevices(this.selectedGroupId, true);
    this.loadStatusCount(this.selectedGroupId);
    this.fleetDropdownOpen = false;
  }

  toggleRefreshDropdown(): void {
    this.refreshDropdownOpen = !this.refreshDropdownOpen;
    this.fleetDropdownOpen = false;
    if (this.refreshDropdownOpen) {
      this.customerDropdown?.close();
    }
  }

  closeRefreshDropdown(): void {
    this.refreshDropdownOpen = false;
  }

  selectRefresh(option: { label: string; value: number }): void {
    this.selectedRefresh = option;
    this.refreshDropdownOpen = false;
    // Restart the timer so the new cadence takes effect immediately.
    if (this.useLiveApi) {
      this.startAutoRefresh();
    }
  }

  refreshNow(): void {
    if (this.useLiveApi) {
      this.loadDevices(this.selectedGroupId, !this.selectedDevice);
      this.loadStatusCount(this.selectedGroupId);
      this.startAutoRefresh();
    }
  }

  toggleGroupByDropdown(): void {
    this.groupByDropdownOpen = !this.groupByDropdownOpen;
    this.statusFilterDropdownOpen = false;
  }

  selectGroupBy(option: string): void {
    this.selectedGroupBy = option;
    this.groupByDropdownOpen = false;
  }

  toggleStatusFilterDropdown(): void {
    this.statusFilterDropdownOpen = !this.statusFilterDropdownOpen;
    this.groupByDropdownOpen = false;
  }

  selectStatusFilter(option: string): void {
    this.selectedStatusFilter = option;
    this.statusFilterDropdownOpen = false;
    this.loadDevices(this.selectedGroupId, true);
  }

  selectCommunicationFilter(
    filter: 'communicating' | 'notCommunicating' | 'disconnected',
  ): void {
    const count =
      filter === 'communicating'
        ? this.statusCount.communicating
        : filter === 'notCommunicating'
          ? this.notCommunicatingCount
          : this.statusCount.disconnected;
    if (count === 0) {
      return;
    }

    this.selectedCommunicationFilter =
      this.selectedCommunicationFilter === filter ? 'all' : filter;

    this.loadDevices(this.selectedGroupId, true);
  }

  /** Filtered dataset evaluating search matching (trip status is filtered server-side). */
  get activeMapDevices(): LiveDevice[] {
    const searchQueryNormalized = this.searchQuery.trim().toLowerCase();
    if (!searchQueryNormalized) {
      return this.devices;
    }
    return this.devices.filter(
      (d) =>
        d.id.toLowerCase().includes(searchQueryNormalized) ||
        (d.vin ?? '').toLowerCase().includes(searchQueryNormalized),
    );
  }

    /** Dynamically fits the map viewport around all currently available device coordinates */
    private fitMapToDevices(): void {
      if (!this.map || this.devices.length === 0) return;
      this.hasFitInitialView = true;
      const bounds = new google.maps.LatLngBounds();
      // Span the calculation box to grab every single vehicle position coordinate
      this.devices.forEach((d) => {
        bounds.extend({ lat: d.lat, lng: d.lng });
      });

      // Fit the viewport with 60px edge-padding to avoid cutting off markers near toolbars
      this.map.fitBounds(bounds, 60);

      // Avoid over-zooming on initial load if only 1 device is in the array
      const listener = this.map.addListener('idle', () => {
        if (this.map.getZoom() > 14) {
          this.map.setZoom(12);
        }
        google.maps.event.removeListener(listener);
      });
    }

  // ---------- Map ----------

  private async initMap(): Promise<void> {
    if (!this.mapCanvasRef?.nativeElement || typeof google === 'undefined') {
      return;
    }

    try {
      await google.maps.importLibrary('marker');

      this.map = new google.maps.Map(this.mapCanvasRef.nativeElement, {
        center: this.FALLBACK_CENTER,
        zoom: 2,
        mapId: 'LN_FLEET_MAP_ID',
        disableDefaultUI: true,
        clickableIcons: false,
        gestureHandling: 'greedy',
        styles: [
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
        ],
      });

      this.map.addListener('click', () => {
        this.zone.run(() => {
          if (!this.selectedDevice) {
            return;
          }
          this.selectedDevice = null;
          this.drawMarkers();
          this.fitMapToDevices();
          this.cdr.markForCheck();
        });
      });

      this.drawMarkers();
      if (this.selectedDevice && this.hasValidCoords(this.selectedDevice)) {
        this.panToDevice(this.selectedDevice);
      } else {
        this.fitMapToDevices();
      }
    } catch (error) {
      console.error('Failed initialization of advanced map marker libraries:', error);
    }
  }

  // ---------- Native Custom Map Controls ----------

  zoomIn(): void {
    if (this.map) {
      const currentZoom = this.map.getZoom() ?? 12;
      this.map.setZoom(currentZoom + 1);
    }
  }

  zoomOut(): void {
    if (this.map) {
      const currentZoom = this.map.getZoom() ?? 12;
      this.map.setZoom(currentZoom - 1);
    }
  }

  recenterMap(): void {
    if (this.map) {
      this.fitMapToDevices();
    }
  }

  toggleFullscreen(): void {
    if (!this.mapCanvasRef?.nativeElement) return;
    const mapWrapperElement = this.mapCanvasRef.nativeElement.parentElement;

    if (!document.fullscreenElement) {
      mapWrapperElement.requestFullscreen().catch((err: any) => {
        console.error(`Error enabling fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  }

  setMapType(type: 'roadmap' | 'hybrid'): void {
    if (this.map) {
      this.currentMapType = type;
      this.map.setMapTypeId(type);
    }
  }

  /** Accommodates long-distance camera movements by smoothly stepping into bounds */
  private smoothZoomToBounds(bounds: google.maps.LatLngBounds): void {
    if (!this.map || typeof google === 'undefined') return;

    // 1. Center the viewport onto the exact geometric midpoint of the cluster
    const targetCenter = bounds.getCenter();
    this.map.panTo(targetCenter);

    // 2. Synchronously calculate the precise target zoom depth that fits the bounding box
    const targetZoom = this.calculateTargetZoom(bounds);

    // 3. Initiate step-by-step smooth zooming animation sequence
    setTimeout(() => {
      this.zone.run(() => {
        this.animateZoomStep(targetZoom);
      });
    }, 150);
  }

  /** Recursively advances the camera viewport zoom level frame by frame */
  private animateZoomStep(targetZoom: number): void {
    if (!this.map) return;
    const currentZoom = this.map.getZoom() ?? 12;
    // Base Case: Target zoom achieved or exceeded safely
    if (currentZoom >= targetZoom) {
      return;
    }

    // Step inward by exactly 1 zoom level
    this.map.setZoom(currentZoom + 1);

    // Schedule next incremental step jump over an optimized delay window
    setTimeout(() => {
      this.zone.run(() => {
        this.animateZoomStep(targetZoom);
      });
    }, 140);
  }

  /** Pure mathematical helper that computes the required zoom index for a bounding box */
  private calculateTargetZoom(bounds: google.maps.LatLngBounds): number {
    const mapElement = this.mapCanvasRef?.nativeElement;
    if (!mapElement) return 14;

    // Fetch the physical live pixel dimensions of your map html layout panel container
    const mapWidth = mapElement.clientWidth || 800;
    const mapHeight = mapElement.clientHeight || 600;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    // Map latitude/longitude span differences into trigonometric angular distance models
    const latFraction = (this.latRad(ne.lat()) - this.latRad(sw.lat())) / Math.PI;
    const lngDiff = ne.lng() - sw.lng();
    const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

    // Calculate maximum zoom limits for both axis lengths independently
    const latZoom = Math.floor(Math.log(mapHeight / 256 / latFraction) / Math.LN2);
    const lngZoom = Math.floor(Math.log(mapWidth / 256 / lngFraction) / Math.LN2);

    // Choose the tighter zoom metric constraint, subtract 1 for edge-padding safety
    const computedZoom = Math.min(latZoom, lngZoom) - 1;

    // Restrict within real bounds (Never over-zoom past max detail street level)
    return Math.max(1, Math.min(computedZoom, 18));
  }

  /** Helper method that maps coordinates into radians matching Mercator maps projections */
  private latRad(lat: number): number {
    const sin = Math.sin(lat * Math.PI / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  private clearMarkers(): void {
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
      this.markerClusterer = null;
    }
    this.mapMarkers.forEach(m => m.map = null);
    this.mapMarkers = [];
  }

  public drawMarkers(): void {
    if (!this.map || typeof google === 'undefined' || !google.maps.marker?.AdvancedMarkerElement) {
      return;
    }
    this.clearMarkers();

    this.zone.run(() => {
      const normalizedQuery = this.searchQuery.trim().toLowerCase();
      const devicesToRender = this.selectedDevice
        ? this.activeMapDevices.filter((d) => d.id === this.selectedDevice!.id)
        : this.activeMapDevices;

      devicesToRender.forEach((d, index) => {
        const markerColor = this.markerColorForDevice(d);

        const markerContainer = document.createElement('div');
        markerContainer.style.position = 'relative';
        markerContainer.style.width = '44px';
        markerContainer.style.height = '52px';
        markerContainer.style.cursor = 'pointer';
        markerContainer.className = `ln-custom-marker-wrapper device-marker-${d.id}`;

        const isSearchedMatch = normalizedQuery && d.id.toLowerCase().includes(normalizedQuery);

        if (isSearchedMatch) {
          // Ensure the global style animation rules are injected into the page head safely
          if (!document.getElementById('map-animation-keyframes')) {
            const styleSheet = document.createElement('style');
            styleSheet.id = 'map-animation-keyframes';
            styleSheet.type = 'text/css';
            styleSheet.innerText = `
              @keyframes markerPulseRing {
                0% { transform: scale(0.6); opacity: 1; }
                100% { transform: scale(1.6); opacity: 0; }
              }
            `;
            document.head.appendChild(styleSheet);
          }

          const pulseRing = document.createElement('div');
          pulseRing.style.position = 'absolute';
          pulseRing.style.top = '0';
          pulseRing.style.left = '0';
          pulseRing.style.width = '44px';
          pulseRing.style.height = '44px';
          pulseRing.style.borderRadius = '50%';
          pulseRing.style.border = '4px solid #4f46e5';
          pulseRing.style.boxSizing = 'border-box';
          pulseRing.style.animation = 'markerPulseRing 1.5s infinite ease-out';
          pulseRing.style.pointerEvents = 'none';
          pulseRing.style.zIndex = '1';
          markerContainer.appendChild(pulseRing);
        }

        // 2. Wrap your premium custom SVG code inside a wrapper layer
        const svgWrapper = document.createElement('div');
        svgWrapper.style.position = 'relative';
        svgWrapper.style.zIndex = '2'; // Keeps the pin looking crisp on top of the pulse ring
        svgWrapper.innerHTML = this.getModernVehicleSvg(
          markerColor,
          d.status === 'disconnected',
          `marker-${index}`
        );
        markerContainer.appendChild(svgWrapper);

        // 3. Construct the clean hover tooltip text element node
        const tooltip = document.createElement('div');
        tooltip.innerText = `S/N: ${d.id}`;
        tooltip.style.position = 'absolute';
        tooltip.style.bottom = '58px';
        tooltip.style.left = '50%';
        tooltip.style.transform = 'translateX(-50%)';
        tooltip.style.backgroundColor = '#1e293b';
        tooltip.style.color = '#ffffff';
        tooltip.style.padding = '4px 8px';
        tooltip.style.borderRadius = '4px';
        tooltip.style.fontSize = '11px';
        tooltip.style.fontWeight = '600';
        tooltip.style.whiteSpace = 'nowrap';
        tooltip.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';
        tooltip.style.pointerEvents = 'none';
        tooltip.style.opacity = '0';
        tooltip.style.transition = 'opacity 0.15s ease-in-out, background-color 0.15s ease-in-out';
        tooltip.style.zIndex = '1000';

        markerContainer.appendChild(tooltip);

        // 4. Initialize advanced map tracking element
        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: { lat: d.lat, lng: d.lng },
          content: markerContainer,
          zIndex: isSearchedMatch ? 100000 : undefined,
        });

        (marker as any).deviceStatus = markerColor;
        (marker as any).deviceData = d;

        markerContainer.addEventListener('mouseenter', () => {
          marker.zIndex = 100000;
          if (!isSearchedMatch) {
            tooltip.style.opacity = '1';
          }
        });
        markerContainer.addEventListener('mouseleave', () => {
          marker.zIndex = isSearchedMatch ? 100000 : null;
          if (!isSearchedMatch) {
            tooltip.style.opacity = '0';
          }
        });

        // If matched by search query, enforce permanent visibility and change highlight background color
        if (isSearchedMatch) {
          tooltip.style.opacity = '1';
          tooltip.style.backgroundColor = '#4f46e5';
        }

        markerContainer.addEventListener('click', (e) => {
          e.stopPropagation();
          this.zone.run(() => this.selectDevice(d));
        });
        this.mapMarkers.push(marker);
      });

      // Re-initialize the MarkerClusterer with the newly filtered markers array
      this.markerClusterer = new MarkerClusterer({
        map: this.map,
        markers: this.mapMarkers,
        renderer: {
          render: (cluster: Cluster) => this.renderCustomClusterElement(cluster)
        },
        onClusterClick: (event: any, cluster: Cluster, map: any) => {
          if (!cluster.markers || cluster.markers.length === 0) return;
          const bounds = new google.maps.LatLngBounds();
          cluster.markers.forEach((marker: any) => {
            if (marker.position) bounds.extend(marker.position);
          });
          this.zone.run(() => {
            this.smoothZoomToBounds(bounds);
          });
        }
      });

      this.cdr.markForCheck();
    });
  }


  // /** Renders the cluster background color dynamically based on its dominant state */
  private renderCustomClusterElement(cluster: Cluster): any {
    const count = cluster.count;

    const stats = { green: 0, yellow: 0, red: 0 };
    cluster.markers.forEach((marker: any) => {
      const status: 'green' | 'yellow' | 'red' = marker.deviceStatus || 'green';
      stats[status]++;
    });

    let dominantColor: 'green' | 'yellow' | 'red' = 'green';
    let maxCount = stats.green;

    if (stats.yellow > maxCount) {
      dominantColor = 'yellow';
      maxCount = stats.yellow;
    }
    if (stats.red > maxCount) {
      dominantColor = 'red';
    }

    const colors = this.getClusterColors(dominantColor);

    const clusterElement = document.createElement('div');
    clusterElement.style.width = '44px';
    clusterElement.style.height = '44px';
    clusterElement.style.borderRadius = '50%';
    clusterElement.style.background = `linear-gradient(135deg, ${colors.light} 0%, ${colors.main} 100%)`;
    clusterElement.style.border = '2px solid #ffffff';
    clusterElement.style.color = '#ffffff';
    clusterElement.style.fontWeight = '700';
    clusterElement.style.fontSize = '14px';
    clusterElement.style.display = 'flex';
    clusterElement.style.alignItems = 'center';
    clusterElement.style.justifyContent = 'center';
    clusterElement.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.35), inset 0 2px 4px rgba(255, 255, 255, 0.4)';
    clusterElement.style.textShadow = '0 1px 2px rgba(0, 0, 0, 0.3)';
    clusterElement.style.cursor = 'pointer';
    clusterElement.innerText = String(count);

    // Wrap the badge so we can anchor a hover tooltip listing every serial in the cluster.
    const wrapper = document.createElement('div');
    wrapper.style.position = 'relative';
    wrapper.style.width = '44px';
    wrapper.style.height = '44px';
    wrapper.appendChild(clusterElement);

    const tooltip = this.buildClusterTooltip(cluster, colors);
    wrapper.appendChild(tooltip);

    // Reveal on hover. mouseleave does not fire when moving onto child nodes (badge/tooltip),
    // and the tooltip's transparent bottom padding bridges the gap so the list stays open
    // while the cursor travels up from the badge.
    const clusterMarker = new google.maps.marker.AdvancedMarkerElement({
      position: cluster.position,
      content: wrapper,
    });
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    const show = () => {
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      clusterMarker.zIndex = 100000;
      tooltip.style.opacity = '1';
      tooltip.style.transform = 'translateX(-50%) translateY(0)';
      tooltip.style.pointerEvents = 'auto';
    };
    const hide = () => {
      hideTimer = setTimeout(() => {
        clusterMarker.zIndex = null;
        tooltip.style.opacity = '0';
        tooltip.style.transform = 'translateX(-50%) translateY(6px)';
        tooltip.style.pointerEvents = 'none';
      }, 120);
    };
    wrapper.addEventListener('mouseenter', show);
    wrapper.addEventListener('mouseleave', hide);

    return clusterMarker;
  }

  /**
   * Builds the scrollable, clickable serial-number tooltip shown when hovering a cluster.
   * Styling mirrors the single-marker "S/N" tooltip (dark slate surface, white text) while
   * adding a bounded, scrollable list whose rows navigate to the chosen device on click.
   */
  private buildClusterTooltip(cluster: Cluster, colors: { main: string; light: string }): HTMLElement {
    this.injectClusterTooltipStyles();

    const tooltip = document.createElement('div');
    tooltip.style.position = 'absolute';
    tooltip.style.bottom = '100%';
    tooltip.style.left = '50%';
    tooltip.style.transform = 'translateX(-50%) translateY(6px)';
    tooltip.style.width = '200px';
    tooltip.style.padding = '0 0 10px 0';
    tooltip.style.background = 'transparent';
    tooltip.style.opacity = '0';
    tooltip.style.pointerEvents = 'none';
    tooltip.style.transition = 'opacity 0.15s ease-in-out, transform 0.15s ease-in-out';
    tooltip.style.zIndex = '1000';

    // Card: the single visible dark surface.
    const card = document.createElement('div');
    card.style.backgroundColor = '#1e293b';
    card.style.color = '#ffffff';
    card.style.borderRadius = '10px';
    card.style.border = '1px solid rgba(255, 255, 255, 0.08)';
    card.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.45)';
    card.style.overflow = 'hidden';
    tooltip.appendChild(card);

    // Header: count summary, accented with the cluster's dominant color.
    const header = document.createElement('div');
    header.innerText = `${cluster.count} devices`;
    header.style.padding = '9px 12px';
    header.style.fontSize = '11px';
    header.style.fontWeight = '700';
    header.style.letterSpacing = '0.4px';
    header.style.textTransform = 'uppercase';
    header.style.color = '#f8fafc';
    header.style.borderBottom = '1px solid rgba(255, 255, 255, 0.1)';
    header.style.borderLeft = `3px solid ${colors.main}`;
    card.appendChild(header);

    // Scrollable list of serial numbers.
    const list = document.createElement('div');
    list.className = 'ln-cluster-tooltip-scroll';
    list.style.maxHeight = '180px';
    list.style.overflowY = 'auto';
    list.style.padding = '4px 0';
    // Keep wheel scrolling inside the list instead of zooming the map underneath.
    list.addEventListener('wheel', (e) => e.stopPropagation());

    cluster.markers?.forEach((marker: any) => {
      const device: LiveDevice | undefined = marker.deviceData;
      if (!device) return;

      const row = document.createElement('button');
      row.type = 'button';
      row.innerText = `S/N: ${device.id}`;
      row.style.display = 'block';
      row.style.width = '100%';
      row.style.textAlign = 'left';
      row.style.padding = '6px 12px';
      row.style.border = 'none';
      row.style.background = 'transparent';
      row.style.color = '#e2e8f0';
      row.style.fontSize = '11px';
      row.style.fontWeight = '600';
      row.style.fontFamily = 'inherit';
      row.style.cursor = 'pointer';
      row.style.whiteSpace = 'nowrap';
      row.style.overflow = 'hidden';
      row.style.textOverflow = 'ellipsis';
      row.style.transition = 'background-color 0.12s ease-in-out, color 0.12s ease-in-out';

      row.addEventListener('mouseenter', () => {
        row.style.backgroundColor = '#4f46e5';
        row.style.color = '#ffffff';
      });
      row.addEventListener('mouseleave', () => {
        row.style.backgroundColor = 'transparent';
        row.style.color = '#e2e8f0';
      });
      row.addEventListener('click', (e) => {
        e.stopPropagation();
        this.zone.run(() => this.selectDevice(device));
      });

      list.appendChild(row);
    });
    card.appendChild(list);

    return tooltip;
  }

  /** Injects the thin custom scrollbar styling for the cluster tooltip list (once). */
  private injectClusterTooltipStyles(): void {
    if (document.getElementById('ln-cluster-tooltip-styles')) return;
    const style = document.createElement('style');
    style.id = 'ln-cluster-tooltip-styles';
    style.innerText = `
      .ln-cluster-tooltip-scroll::-webkit-scrollbar { width: 6px; }
      .ln-cluster-tooltip-scroll::-webkit-scrollbar-track { background: transparent; }
      .ln-cluster-tooltip-scroll::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.22);
        border-radius: 3px;
      }
      .ln-cluster-tooltip-scroll::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.35);
      }
      .ln-cluster-tooltip-scroll {
        scrollbar-width: thin;
        scrollbar-color: rgba(255, 255, 255, 0.22) transparent;
      }
    `;
    document.head.appendChild(style);
  }

  private getClusterColors(color: 'green' | 'yellow' | 'red'): { main: string; light: string } {
    switch (color) {
      case 'green':  return { main: '#10b77f', light: '#34d399' };
      case 'yellow': return { main: '#eab308', light: '#fde047' };
      case 'red':    return { main: '#ef4444', light: '#f87171' };
      default:       return { main: '#10b77f', light: '#34d399' };
    }
  }

  private getModernVehicleSvg(
    color: 'green' | 'yellow' | 'red',
    disconnected: boolean,
    uniqueKey: string
  ): string {
    const colorMap = {
      green:  { base: '#10b77f', gradient: '#059669' },
      yellow: { base: '#facc15', gradient: '#ca8a04' },
      red:    { base: '#ef4444', gradient: '#dc2626' }
    };

    const palette = colorMap[color];
    const gradId = `pinGrad-${color}-${uniqueKey.replace(/[^a-zA-Z0-9_-]/g, '_')}`;

    return `
    <svg xmlns="http://w3.org" width="44" height="52" viewBox="0 0 44 52" style="filter: drop-shadow(0px 5px 8px rgba(0,0,0,0.35)); transition: filter 0.3s ease;">
      <defs>
        <linearGradient id="${gradId}" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="${palette.base}" />
          <stop offset="100%" stop-color="${palette.gradient}" />
        </linearGradient>
      </defs>

      <!-- Outer Teardrop Pin Silhouette using unique grad id references -->
      <path d="M22 0C9.8 0 0 9.8 0 22c0 14.4 19.3 28.3 21.2 29.6.5.3 1.1.3 1.6 0C24.7 50.3 44 36.4 44 22 44 9.8 34.2 0 22 0z" fill="url(#${gradId})"/>

      <!-- Inner White Spotlight Container Ring -->
      <circle cx="22" cy="21" r="14" fill="#ffffff"/>

      <!-- Font Awesome "fa-car" (solid) glyph badge, scaled into the white spotlight ring -->
      <g transform="translate(13, 12) scale(0.036)" fill="url(#${gradId})">
        <path d="M135.2 117.4L109.1 192H402.9l-26.1-74.6C372.3 104.6 360.2 96 346.6 96H165.4c-13.6 0-25.7 8.6-30.2 21.4zM39.6 196.8L74.8 96.3C88.3 57.8 124.6 32 165.4 32H346.6c40.8 0 77.1 25.8 90.6 64.3l35.2 100.5c23.2 9.6 39.6 32.5 39.6 59.2V400v48c0 17.7-14.3 32-32 32H448c-17.7 0-32-14.3-32-32V400H96v48c0 17.7-14.3 32-32 32H32c-17.7 0-32-14.3-32-32V400 256c0-26.7 16.4-49.6 39.6-59.2zM128 288a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm288 32a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/>
      </g>
    </svg>`;
  }


  // ---------- Helpers used by the template ----------

    /** Copies device serial numbers to system clipboard */
    copySerialNumber(event: MouseEvent, serialNumber: string): void {
      // Prevent the click from selecting the row item on the sidebar map
      event.stopPropagation();
  
      if (!navigator.clipboard) {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = serialNumber;
        textarea.style.position = 'fixed'; // Avoid scrolling view
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          console.log('Serial number copied to clipboard (fallback)');
        } catch (err) {
          console.error('Fallback copy method failed', err);
        }
        document.body.removeChild(textarea);
        return;
      }
  
      // Modern standard clipboard operation API
      navigator.clipboard.writeText(serialNumber)
        .then(() => {
          console.log(`Serial number ${serialNumber} copied successfully!`);
          // Optional: You could trigger a small toast notification here
        })
        .catch(err => {
          console.error('Could not copy string text: ', err);
        });
    }
  

  statusDotClass(status: DeviceStatus): string {
    return `status-dot status-dot--${status}`;
  }

  vehicleIconClass(color: LiveDevice['vehicleColor']): string {
    return `ln-device__vehicle--${color}`;
  }

  trackById(device: LiveDevice, index: number): string {
    return `${device.id}_${index}`;
  }

  viewDeviceDetails(id: string): void {
    this.router.navigate(['/devices', id]);
  }

  goToTripDashboard(event: Event, device: LiveDevice | null | undefined): void {
    event?.stopPropagation();
    if (!device?.deviceId && !device?.serialNumber && !device?.id) {
      return;
    }
    this.deviceNavigation.goToTripDashboard({
      deviceId: device.deviceId,
      serialNumber: device.serialNumber || device.id,
      assetName: device.assetName,
      groupId: this.selectedGroupId || undefined,
    });
  }
}
